import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { INDIAN_STATES, deriveFromGstin, validateGstin } from '../utils/gst';
import ConfirmDialog from '../components/ConfirmDialog';
import SearchableSelect from '../components/SearchableSelect';

function LogoUpload() {
  const qc = useQueryClient();
  const [preview, setPreview] = useState(null);
  const { data: biz } = useQuery({ queryKey:['business'], queryFn:()=>api.get('/clients/business').then(r=>r.data) });

  const uploadMutation = useMutation({
    mutationFn: (file) => { const fd=new FormData(); fd.append('logo',file); return api.post('/clients/business/logo',fd,{headers:{'Content-Type':'multipart/form-data'}}); },
    onSuccess: () => { toast.success('Logo uploaded — will appear on all new invoices'); qc.invalidateQueries(['business']); },
    onError: () => toast.error('Logo upload failed'),
  });

  const existingLogoUrl = biz?.logo_path ? `/uploads/logo/${biz.logo_path}?t=${Date.now()}` : null;

  return (
    <div>
      <div style={{fontSize:11,color:'var(--text3)',fontWeight:700,marginBottom:8}}>INVOICE LOGO (appears on printed invoices)</div>
      <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
        <div style={{width:160,height:60,border:'2px dashed var(--border)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',background:'var(--surface2)',overflow:'hidden',flexShrink:0}}>
          {(preview||existingLogoUrl)
            ? <img src={preview||existingLogoUrl} alt="Logo" style={{maxWidth:'100%',maxHeight:'100%',objectFit:'contain'}}/>
            : <span style={{fontSize:11,color:'var(--text3)'}}>No logo yet</span>
          }
        </div>
        <div>
          <label style={{cursor:'pointer'}}>
            <span className="btn-sm" style={{display:'inline-block'}}>Upload logo (PNG, JPG, SVG — max 5MB)</span>
            <input type="file" accept="image/*" style={{display:'none'}} onChange={e=>{
              const f=e.target.files[0]; if(!f) return;
              setPreview(URL.createObjectURL(f));
              uploadMutation.mutate(f);
            }}/>
          </label>
          {existingLogoUrl&&<div style={{fontSize:11,color:'var(--green)',marginTop:6}}>✓ Logo saved · will embed in invoices as-is</div>}
          {uploadMutation.isPending&&<div style={{fontSize:11,color:'var(--text2)',marginTop:6}}>Uploading…</div>}
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const qc = useQueryClient();
  const [catForm, setCatForm] = useState({ name:'', default_price:'', gst_percent:'18', client_id:'' });
  const [editCat, setEditCat] = useState(null); // category being edited
  const [editCatForm, setEditCatForm] = useState({ name:'', default_price:'', gst_percent:'' });
  const [corrTypeForm, setCorrTypeForm] = useState({ name:'', default_price:'500', gst_percent:'18' });
  const [editCorrType, setEditCorrType] = useState(null);
  const [editCorrTypeForm, setEditCorrTypeForm] = useState({ name:'', default_price:'', gst_percent:'' });
  const [userForm, setUserForm] = useState({ username:'', full_name:'', password:'', role:'team', _empId:'' });
  const [bizForm, setBizForm] = useState({ name:'', gstin:'', pan:'', cin:'', phone:'', email:'', address:'', state:'Maharashtra', correction_rate:'500', correction_gst_percent:'18', bank_name:'', bank_account_name:'', bank_account_no:'', bank_ifsc:'' });
  const [pwForm, setPwForm] = useState({ currentPassword:'', newPassword:'' });
  const [myDob, setMyDob] = useState('');
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteCat, setDeleteCat] = useState(null);
  const [resetUser, setResetUser] = useState(null); // user object
  const [resetPw, setResetPw] = useState('');
  const [editUser, setEditUser] = useState(null); // user object
  const [editUserForm, setEditUserForm] = useState({ full_name:'', username:'', role:'', date_of_birth:'' });

  const { data: categories=[] } = useQuery({ queryKey:['categories'], queryFn:()=>api.get('/categories').then(r=>r.data) });
  const { data: corrTypes=[] } = useQuery({ queryKey:['correction-types'], queryFn:()=>api.get('/correction-types').then(r=>r.data) });
  const { data: users=[] } = useQuery({ queryKey:['users'], queryFn:()=>api.get('/users').then(r=>r.data) });
  const { data: clients=[] } = useQuery({ queryKey:['clients'], queryFn:()=>api.get('/clients').then(r=>r.data) });
  const { data: allEmployees=[] } = useQuery({ queryKey:['hr-employees'], queryFn:()=>api.get('/hr/employees').then(r=>r.data) });
  // Employees without a login yet — available to grant access
  const employeesWithoutAccess = allEmployees.filter(e => !e.user_id);

  useEffect(()=>{
    api.get('/users').then(r=>{
      const me = Array.isArray(r.data) ? r.data[0] : r.data;
      if (me?.date_of_birth) setMyDob(me.date_of_birth.slice(0,10));
    }).catch(()=>{});
  },[]);

  useEffect(()=>{
    api.get('/clients/business').then(r=>{
      if(r.data) setBizForm({
        name:r.data.name||'', gstin:r.data.gstin||'', pan:r.data.pan||'', cin:r.data.cin||'',
        phone:r.data.phone||'', email:r.data.email||'', address:r.data.address||'',
        state:r.data.state||'Maharashtra', correction_rate:r.data.correction_rate||'500',
        correction_gst_percent:r.data.correction_gst_percent||'18',
        bank_name:r.data.bank_name||'', bank_account_name:r.data.bank_account_name||'',
        bank_account_no:r.data.bank_account_no||'', bank_ifsc:r.data.bank_ifsc||'',
      });
    }).catch(()=>{});
  },[]);

  const addCat = useMutation({
    mutationFn: d=>api.post('/categories',d),
    onSuccess: ()=>{ toast.success('Category added'); qc.invalidateQueries(['categories']); setCatForm({name:'',default_price:'',gst_percent:'18',client_id:''}); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to add — check for duplicates'),
  });
  const updateCat = useMutation({
    mutationFn: ({id,...d})=>api.patch(`/categories/${id}`,d),
    onSuccess: ()=>{ toast.success('Category updated'); qc.invalidateQueries(['categories']); setEditCat(null); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to update category'),
  });
  const removeCat = useMutation({
    mutationFn: id=>api.delete(`/categories/${id}`),
    onSuccess: ()=>{ qc.invalidateQueries(['categories']); setDeleteCat(null); },
    onError: ()=>toast.error('Failed to remove category'),
  });
  const addUser = useMutation({
    mutationFn: ({empId,...d})=>api.post(`/hr/employees/${empId}/grant-access`,d),
    onSuccess: ()=>{
      toast.success('Access granted');
      qc.invalidateQueries(['users']);
      qc.invalidateQueries(['hr-employees']);
      setUserForm({username:'',full_name:'',password:'',role:'team',_empId:''});
    },
    onError: err=>toast.error(err.response?.data?.error||'Failed to grant access'),
  });
  const removeUser = useMutation({
    mutationFn: id=>api.delete(`/users/${id}`),
    onSuccess: ()=>{ qc.invalidateQueries(['users']); setDeleteUser(null); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to remove user'),
  });
  const resetPassword = useMutation({
    mutationFn: ({id,password})=>api.patch(`/users/${id}`,{password}),
    onSuccess: ()=>{ toast.success('Password reset successfully'); setResetUser(null); setResetPw(''); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to reset password'),
  });
  const updateUser = useMutation({
    mutationFn: ({id,...d})=>api.patch(`/users/${id}`,d),
    onSuccess: ()=>{ toast.success('Member updated'); qc.invalidateQueries(['users']); setEditUser(null); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to update'),
  });
  const saveBiz = useMutation({
    mutationFn: d=>api.patch('/clients/business',d),
    onSuccess: ()=>toast.success('Business details saved'),
    onError: err=>toast.error(err.response?.data?.error||'Failed to save business details'),
  });
  const changePw = useMutation({
    mutationFn: d=>api.post('/auth/change-password',d),
    onSuccess: ()=>{ toast.success('Password changed'); setPwForm({currentPassword:'',newPassword:''}); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to change password — check current password'),
  });
  const saveDob = useMutation({
    mutationFn: dob=>api.patch('/auth/dob',{date_of_birth:dob}),
    onSuccess: ()=>toast.success('Birthday saved'),
    onError: ()=>toast.error('Failed to save birthday'),
  });

  const addCorrType = useMutation({
    mutationFn: d=>api.post('/correction-types',d),
    onSuccess: ()=>{ toast.success('Correction type added'); qc.invalidateQueries(['correction-types']); setCorrTypeForm({name:'',default_price:'500',gst_percent:'18'}); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to add — check for duplicates'),
  });
  const updateCorrType = useMutation({
    mutationFn: ({id,...d})=>api.patch(`/correction-types/${id}`,d),
    onSuccess: ()=>{ toast.success('Updated'); qc.invalidateQueries(['correction-types']); setEditCorrType(null); },
    onError: err=>toast.error(err.response?.data?.error||'Failed to update'),
  });
  const removeCorrType = useMutation({
    mutationFn: id=>api.delete(`/correction-types/${id}`),
    onSuccess: ()=>{ toast.success('Removed'); qc.invalidateQueries(['correction-types']); },
    onError: ()=>toast.error('Failed to remove'),
  });

  const globalCats = categories.filter(c=>!c.client_id);
  const clientCats = categories.filter(c=>c.client_id);

  function openEditCat(c) {
    setEditCat(c);
    setEditCatForm({ name:c.name, default_price:c.default_price, gst_percent:c.gst_percent });
  }

  function CatRow({ c }) {
    return (
      <tr key={c.id}>
        <td>{c.name}</td>
        <td>₹{c.default_price}</td>
        <td>{c.gst_percent}%</td>
        <td style={{whiteSpace:'nowrap',textAlign:'right'}}>
          <button className="btn-sm" style={{marginRight:4}} onClick={()=>openEditCat(c)}>✎</button>
          <button className="btn-danger btn-sm" onClick={()=>setDeleteCat(c.id)}>✕</button>
        </td>
      </tr>
    );
  }

  const { section = 'business' } = useParams();

  return (
    <div>
      <div className="section-head"><h2>Settings</h2></div>

      {/* ── Business & GST ── */}
      {section==='business' && <div className="card" style={{marginBottom:16}}>
        <div className="card-title">Your business & GST details</div>
        <div className="form-grid">
          <div className="field"><label>Studio / Business name</label><input value={bizForm.name} onChange={e=>setBizForm(f=>({...f,name:e.target.value}))} placeholder="8D Studios"/></div>
          <div className="field">
            <label>GSTIN</label>
            {(()=>{
              const derived = deriveFromGstin(bizForm.gstin);
              const gv = validateGstin(bizForm.gstin);
              return <>
                <input
                  value={bizForm.gstin}
                  onChange={e=>{
                    const val = e.target.value.toUpperCase();
                    const d = deriveFromGstin(val);
                    setBizForm(f=>({...f, gstin:val, ...(d ? {state:d.state} : {})}));
                  }}
                  placeholder="27BXOPK0627D1ZK"
                  maxLength={15}
                  style={bizForm.gstin && !gv.valid ? {borderColor:'var(--red)'} : {}}
                />
                {bizForm.gstin && !gv.valid && <div style={{fontSize:11,color:'var(--red)',marginTop:3}}>{gv.error}</div>}
                {derived && <div style={{fontSize:11,color:'var(--green)',marginTop:3}}>✓ Code <strong>{derived.code}</strong> — {derived.state}</div>}
              </>;
            })()}
          </div>
        </div>
        <div className="form-grid">
          <div className="field"><label>PAN Number</label><input value={bizForm.pan} onChange={e=>setBizForm(f=>({...f,pan:e.target.value}))} placeholder="BXOPK0627D"/></div>
          <div className="field"><label>CIN (optional)</label><input value={bizForm.cin} onChange={e=>setBizForm(f=>({...f,cin:e.target.value}))} placeholder="U74999MH2020PTC123456"/></div>
        </div>
        <div className="form-grid">
          <div className="field"><label>Phone</label><input value={bizForm.phone} onChange={e=>setBizForm(f=>({...f,phone:e.target.value}))} placeholder="8888 728 658"/></div>
          <div className="field"><label>Email</label><input type="email" value={bizForm.email} onChange={e=>setBizForm(f=>({...f,email:e.target.value}))} placeholder="design@8dstudios.com"/></div>
        </div>
        <div className="field"><label>Address</label><textarea rows={2} value={bizForm.address} onChange={e=>setBizForm(f=>({...f,address:e.target.value}))} placeholder="F9 - Santakrupa Angan, Madhuban society, Lane number 4, old Sangavi, Pune 411027 Maharashtra"/></div>
        <div className="form-grid">
          <div className="field">
            <label>Your state {deriveFromGstin(bizForm.gstin) && <span style={{fontSize:10,color:'var(--accent)',fontWeight:600}}>(auto from GSTIN)</span>}</label>
            <select
              value={bizForm.state}
              onChange={e=>setBizForm(f=>({...f,state:e.target.value}))}
              disabled={!!deriveFromGstin(bizForm.gstin)}
              style={deriveFromGstin(bizForm.gstin) ? {opacity:0.7,cursor:'not-allowed'} : {}}
            >
              {INDIAN_STATES.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <hr className="divider" style={{margin:'12px 0'}}/>
        <div style={{fontSize:11,color:'var(--text3)',fontWeight:700,marginBottom:8}}>BANK DETAILS (printed on invoices)</div>
        <div className="form-grid">
          <div className="field"><label>Account holder name</label><input value={bizForm.bank_account_name} onChange={e=>setBizForm(f=>({...f,bank_account_name:e.target.value}))} placeholder="Nitin Kadrekar"/></div>
          <div className="field"><label>Account number</label><input value={bizForm.bank_account_no} onChange={e=>setBizForm(f=>({...f,bank_account_no:e.target.value}))} placeholder="20160605731"/></div>
        </div>
        <div className="form-grid">
          <div className="field"><label>IFSC code</label><input value={bizForm.bank_ifsc} onChange={e=>setBizForm(f=>({...f,bank_ifsc:e.target.value}))} placeholder="SBIN0014730"/></div>
          <div className="field"><label>Bank name &amp; branch</label><input value={bizForm.bank_name} onChange={e=>setBizForm(f=>({...f,bank_name:e.target.value}))} placeholder="State Bank of India, SANGVI"/></div>
        </div>
        <hr className="divider" style={{margin:'12px 0'}}/>
        <LogoUpload />
        <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
          <button className="btn-primary" onClick={()=>saveBiz.mutate(bizForm)} disabled={saveBiz.isPending}>{saveBiz.isPending?'Saving…':'Save details'}</button>
        </div>
      </div>}

      {/* ── Categories & Pricing ── */}
      {section==='categories' && (() => {
        // Group client-specific cats by client_id
        const grouped = {};
        clientCats.forEach(c => { if(!grouped[c.client_id]) grouped[c.client_id]=[]; grouped[c.client_id].push(c); });

        function ClientCatCard({ clientId, cats }) {
          const client = clients.find(c=>c.id===clientId);
          const [expanded, setExpanded] = React.useState(false);
          const [showAdd, setShowAdd] = React.useState(false);
          const [form, setForm] = React.useState({ name:'', default_price:'', gst_percent:'18' });
          return (
            <div className="card" style={{marginBottom:12,padding:0,overflow:'hidden'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 16px',cursor:'pointer'}}
                onClick={()=>setExpanded(v=>!v)}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{display:'inline-block',transition:'transform 0.2s',transform:expanded?'rotate(90deg)':'none',fontSize:11,color:'var(--text3)'}}>▶</span>
                  <div>
                    <div style={{fontWeight:700,fontSize:14}}>{client?.name||'Unknown Client'}</div>
                    <div style={{fontSize:11,color:'var(--text2)',marginTop:2}}>{cats.length} service item{cats.length!==1?'s':''}</div>
                  </div>
                </div>
                <button className="btn-sm" style={{background:'var(--accent)',color:'#fff',fontWeight:600}}
                  onClick={e=>{e.stopPropagation();setExpanded(true);setShowAdd(v=>!v);}}>
                  {showAdd ? '✕ Cancel' : '+ Add item'}
                </button>
              </div>
              {expanded && (
                <div style={{padding:'0 16px 16px'}}>
                  <div style={{maxHeight:400,overflowY:'auto',border:'1px solid var(--border)',borderRadius:8}}>
                    <table style={{width:'100%',fontSize:12}}>
                      <thead><tr><th>Service</th><th>Price</th><th>GST%</th><th></th></tr></thead>
                      <tbody>{cats.map(c=>(
                        <tr key={c.id}>
                          <td>{c.name}</td>
                          <td>₹{parseFloat(c.default_price).toLocaleString('en-IN')}</td>
                          <td>{c.gst_percent}%</td>
                          <td style={{whiteSpace:'nowrap',textAlign:'right'}}>
                            <button className="btn-sm" style={{marginRight:4}} onClick={()=>openEditCat(c)}>✎</button>
                            <button className="btn-danger btn-sm" onClick={()=>setDeleteCat(c.id)}>✕</button>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                  {showAdd && (
                    <div style={{borderTop:'1px solid var(--border)',paddingTop:12,marginTop:12}}>
                      <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',marginBottom:10}}>ADD ITEM FOR {(client?.name||'').toUpperCase()}</div>
                      <div className="field"><label>Service name</label>
                        <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Logo Redesign" autoFocus/>
                      </div>
                      <div className="form-grid">
                        <div className="field"><label>Price (₹)</label><input type="number" value={form.default_price} onChange={e=>setForm(f=>({...f,default_price:e.target.value}))}/></div>
                        <div className="field"><label>GST %</label><input type="number" value={form.gst_percent} onChange={e=>setForm(f=>({...f,gst_percent:e.target.value}))}/></div>
                      </div>
                      <button className="btn-primary btn-sm" disabled={addCat.isPending} onClick={()=>{
                        if(!form.name||!form.default_price){toast.error('Fill name and price');return;}
                        addCat.mutate({...form,client_id:clientId},{onSuccess:()=>{setForm({name:'',default_price:'',gst_percent:'18'});setShowAdd(false);}});
                      }}>{addCat.isPending?'Adding…':'Add item'}</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }

        function GlobalCatCard() {
          const [expanded, setExpanded] = React.useState(false);
          return (
            <div className="card" style={{marginBottom:16,padding:0,overflow:'hidden'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 16px',cursor:'pointer'}}
                onClick={()=>setExpanded(v=>!v)}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{display:'inline-block',transition:'transform 0.2s',transform:expanded?'rotate(90deg)':'none',fontSize:11,color:'var(--text3)'}}>▶</span>
                  <div>
                    <div style={{fontWeight:700,fontSize:14}}>🌐 Global — All Clients</div>
                    <div style={{fontSize:11,color:'var(--text2)',marginTop:2}}>{globalCats.length} service item{globalCats.length!==1?'s':''}</div>
                  </div>
                </div>
              </div>
              {expanded && (
                <div style={{padding:'0 16px 16px'}}>
                  <div style={{maxHeight:400,overflowY:'auto',border:'1px solid var(--border)',borderRadius:8}}>
                    <table style={{width:'100%',fontSize:12}}>
                      <thead><tr><th>Service</th><th>Price</th><th>GST%</th><th></th></tr></thead>
                      <tbody>{globalCats.map(c=><CatRow key={c.id} c={c}/>)}</tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        }

        return (
          <div>
            {/* Add new category — global or new client */}
            <div className="card" style={{marginBottom:16}}>
              <div className="card-title">Add category</div>
              <div className="field"><label>Category name</label><input value={catForm.name} onChange={e=>setCatForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Investor Deck"/></div>
              <div className="form-grid">
                <div className="field"><label>Price (₹)</label><input type="number" value={catForm.default_price} onChange={e=>setCatForm(f=>({...f,default_price:e.target.value}))}/></div>
                <div className="field"><label>GST %</label><input type="number" value={catForm.gst_percent} onChange={e=>setCatForm(f=>({...f,gst_percent:e.target.value}))}/></div>
              </div>
              <div className="field"><label>Client (leave blank for global)</label>
                <SearchableSelect value={catForm.client_id} onChange={id=>setCatForm(f=>({...f,client_id:id}))}
                  items={clients} allowClear clearLabel="Global — all clients" placeholder="Global — all clients" />
              </div>
              <button className="btn-primary btn-sm" onClick={()=>{if(!catForm.name||!catForm.default_price){toast.error('Fill category name and price');return;}addCat.mutate({...catForm,client_id:catForm.client_id||undefined});}} disabled={addCat.isPending}>Add category</button>
            </div>

            {/* Per-client cards — shown first so client-specific pricing isn't buried under the global list */}
            {Object.entries(grouped).map(([clientId, cats])=>(
              <ClientCatCard key={clientId} clientId={clientId} cats={cats}/>
            ))}

            {/* Global categories */}
            {globalCats.length>0 && <GlobalCatCard/>}
          </div>
        );
      })()}

      {/* ── Correction Types ── */}
      {section==='corrections' && <div className="form-grid" style={{marginBottom:16}}>
        <div className="card">
          <div className="card-title">Correction types</div>
          <p style={{fontSize:12,color:'var(--text2)',marginBottom:12}}>Pre-defined correction types with rates. When marking a correction on an entry, select the applicable type — the charge gets applied to the invoice automatically.</p>
          {corrTypes.length>0&&(
            <table style={{width:'100%',marginBottom:12,fontSize:12}}>
              <thead><tr><th>Name</th><th>Rate (₹)</th><th>GST%</th><th></th></tr></thead>
              <tbody>{corrTypes.map(t=>(
                <tr key={t.id}>
                  {editCorrType?.id===t.id ? (
                    <>
                      <td><input value={editCorrTypeForm.name} onChange={e=>setEditCorrTypeForm(f=>({...f,name:e.target.value}))} style={{width:'100%'}}/></td>
                      <td><input type="number" value={editCorrTypeForm.default_price} onChange={e=>setEditCorrTypeForm(f=>({...f,default_price:e.target.value}))} style={{width:70}}/></td>
                      <td><input type="number" value={editCorrTypeForm.gst_percent} onChange={e=>setEditCorrTypeForm(f=>({...f,gst_percent:e.target.value}))} style={{width:50}}/></td>
                      <td style={{whiteSpace:'nowrap',textAlign:'right'}}>
                        <button className="btn-primary btn-sm" style={{marginRight:4}} onClick={()=>updateCorrType.mutate({id:t.id,...editCorrTypeForm})} disabled={updateCorrType.isPending}>Save</button>
                        <button className="btn-sm" onClick={()=>setEditCorrType(null)}>✕</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{t.name}</td>
                      <td>₹{t.default_price}</td>
                      <td>{t.gst_percent}%</td>
                      <td style={{whiteSpace:'nowrap',textAlign:'right'}}>
                        <button className="btn-sm" style={{marginRight:4}} onClick={()=>{ setEditCorrType(t); setEditCorrTypeForm({name:t.name,default_price:t.default_price,gst_percent:t.gst_percent}); }}>✎</button>
                        <button className="btn-danger btn-sm" onClick={()=>removeCorrType.mutate(t.id)}>✕</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}</tbody>
            </table>
          )}
          <hr className="divider"/>
          <div style={{fontSize:12,color:'var(--text2)',fontWeight:600,marginBottom:8}}>Add correction type</div>
          <div className="field"><label>Name</label><input value={corrTypeForm.name} onChange={e=>setCorrTypeForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Minor revision"/></div>
          <div className="form-grid">
            <div className="field"><label>Rate (₹)</label><input type="number" value={corrTypeForm.default_price} onChange={e=>setCorrTypeForm(f=>({...f,default_price:e.target.value}))}/></div>
            <div className="field"><label>GST %</label><input type="number" value={corrTypeForm.gst_percent} onChange={e=>setCorrTypeForm(f=>({...f,gst_percent:e.target.value}))}/></div>
          </div>
          <button className="btn-primary btn-sm" onClick={()=>{ if(!corrTypeForm.name||!corrTypeForm.default_price){toast.error('Fill name and rate');return;} addCorrType.mutate(corrTypeForm); }} disabled={addCorrType.isPending}>Add correction type</button>
        </div>

      </div>}

      {/* ── Team Accounts ── */}
      {section==='team' && <div className="form-grid" style={{marginBottom:16}}>
        <div className="card">
          <div className="card-title">Team accounts</div>
          <table style={{width:'100%',marginBottom:12,fontSize:12}}>
            <thead><tr><th>Username</th><th>Name</th><th>Role</th><th>Clock In/Out</th><th></th></tr></thead>
            <tbody>{users.filter(u=>u.role!=='owner').map(u=>(
              <tr key={u.id}><td>{u.username}</td><td>{u.full_name}</td><td>{u.role}</td>
                <td>
                  <button
                    title={u.can_clock ? 'Clock-in enabled — click to disable' : 'Clock-in disabled — click to enable'}
                    onClick={()=>updateUser.mutate({id:u.id, can_clock:!u.can_clock})}
                    style={{padding:'3px 10px',borderRadius:12,border:'1.5px solid',fontSize:11,fontWeight:700,cursor:'pointer',
                      background:u.can_clock?'#DCFCE7':'#F3F4F6',color:u.can_clock?'#15803D':'#9CA3AF',
                      borderColor:u.can_clock?'#16A34A':'#D1D5DB'}}>
                    {u.can_clock ? 'ON' : 'OFF'}
                  </button>
                </td>
                <td style={{whiteSpace:'nowrap',textAlign:'right'}}>
                  <button className="btn-sm" style={{marginRight:4}} title="Edit name / ID" onClick={()=>{ setEditUser(u); setEditUserForm({full_name:u.full_name,username:u.username,role:u.role,date_of_birth:u.date_of_birth?u.date_of_birth.slice(0,10):''}); }}>✎</button>
                  <button className="btn-sm" style={{marginRight:4}} title="Reset password" onClick={()=>{ setResetUser(u); setResetPw(''); }}>🔑</button>
                  <button className="btn-danger btn-sm" onClick={()=>setDeleteUser(u)}>✕</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
          <hr className="divider"/>
          <div style={{fontSize:12,color:'var(--text2)',fontWeight:600,marginBottom:4}}>Grant app access</div>
          <p style={{fontSize:11,color:'var(--text3)',marginBottom:10}}>
            Select an employee to give them an 8D Business OS™ login. Add people first via <strong>HR Modules → Employees</strong>.
          </p>
          {employeesWithoutAccess.length === 0
            ? <div style={{fontSize:12,color:'var(--text3)',fontStyle:'italic',marginBottom:8}}>All employees already have app access.</div>
            : <>
              <div className="field"><label>Employee</label>
                <select value={userForm.username==='' ? '' : userForm._empId||''} onChange={e=>{
                  const emp = allEmployees.find(x=>x.id===e.target.value);
                  setUserForm(f=>({...f, _empId: e.target.value, full_name: emp?.full_name||'', username:'', password:'', role:'team'}));
                }}>
                  <option value="">— Select employee —</option>
                  {employeesWithoutAccess.map(e=><option key={e.id} value={e.id}>{e.full_name}{e.designation ? ` · ${e.designation}` : ''}</option>)}
                </select>
              </div>
              {userForm._empId && <>
                <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>Name: <strong>{userForm.full_name}</strong> (auto-filled from employee record)</div>
                <div className="field"><label>Username (login ID)</label>
                  <input value={userForm.username} onChange={e=>setUserForm(f=>({...f,username:e.target.value}))} placeholder="e.g. priya"/>
                </div>
                <div className="field"><label>Role</label>
                  <select value={userForm.role} onChange={e=>setUserForm(f=>({...f,role:e.target.value}))}>
                    <option value="team">Team — can log work, view own entries</option>
                    <option value="admin">Admin — HR access + manage entries</option>
                    <option value="accountant">Accountant — invoices &amp; finance only</option>
                  </select>
                </div>
                <div className="field"><label>Temporary password (min 6 chars)</label>
                  <input type="text" value={userForm.password} onChange={e=>setUserForm(f=>({...f,password:e.target.value}))} placeholder="Share with them to log in"/>
                </div>
                <button className="btn-primary btn-sm" disabled={addUser.isPending} onClick={()=>{
                  if(!userForm.username||!userForm.password){toast.error('Fill username and password');return;}
                  if(userForm.password.length<6){toast.error('Password min 6 chars');return;}
                  addUser.mutate({ empId: userForm._empId, username: userForm.username, password: userForm.password, role: userForm.role });
                }}>{addUser.isPending?'Granting…':'Grant Access'}</button>
              </>}
            </>
          }
        </div>
      </div>}

      {/* ── Change Password (under Business tab) ── */}
      {section==='business' && <div className="card" style={{maxWidth:420}}>
        <div className="card-title">Change your password</div>
        <div className="field"><label>Current password</label><input type="password" value={pwForm.currentPassword} onChange={e=>setPwForm(f=>({...f,currentPassword:e.target.value}))}/></div>
        <div className="field"><label>New password</label><input type="password" value={pwForm.newPassword} onChange={e=>setPwForm(f=>({...f,newPassword:e.target.value}))}/></div>
        <button className="btn-primary btn-sm" onClick={()=>{if(!pwForm.currentPassword||!pwForm.newPassword){toast.error('Fill both fields');return;}changePw.mutate(pwForm);}} disabled={changePw.isPending}>{changePw.isPending?'Saving…':'Change password'}</button>
      </div>}

      {/* Reset password modal */}
      {resetUser&&(
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:380}}>
            <div className="modal-title">Reset password — {resetUser.full_name}</div>
            <p style={{fontSize:13,color:'var(--text2)',marginBottom:14}}>
              Set a new temporary password for <strong>{resetUser.username}</strong>. Share it with them and ask them to change it after logging in.
            </p>
            <div className="field">
              <label>New password (min 6 chars)</label>
              <input
                type="text"
                value={resetPw}
                onChange={e=>setResetPw(e.target.value)}
                placeholder="e.g. Welcome@123"
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button onClick={()=>setResetUser(null)}>Cancel</button>
              <button className="btn-primary" onClick={()=>{
                if(resetPw.length<6){toast.error('Password must be at least 6 characters');return;}
                resetPassword.mutate({id:resetUser.id,password:resetPw});
              }} disabled={resetPassword.isPending}>
                {resetPassword.isPending?'Resetting…':'Reset password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit category modal */}
      {editCat&&(
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:380}}>
            <div className="modal-title">Edit category</div>
            <div className="field"><label>Name</label><input value={editCatForm.name} onChange={e=>setEditCatForm(f=>({...f,name:e.target.value}))}/></div>
            <div className="form-grid">
              <div className="field"><label>Price (₹)</label><input type="number" value={editCatForm.default_price} onChange={e=>setEditCatForm(f=>({...f,default_price:e.target.value}))}/></div>
              <div className="field"><label>GST %</label><input type="number" value={editCatForm.gst_percent} onChange={e=>setEditCatForm(f=>({...f,gst_percent:e.target.value}))}/></div>
            </div>
            <div className="modal-actions">
              <button onClick={()=>setEditCat(null)}>Cancel</button>
              <button className="btn-primary" onClick={()=>{if(!editCatForm.name||!editCatForm.default_price){toast.error('Fill name and price');return;}updateCat.mutate({id:editCat.id,...editCatForm});}} disabled={updateCat.isPending}>
                {updateCat.isPending?'Saving…':'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete category confirm */}
      <ConfirmDialog
        open={!!deleteCat}
        title="Remove category?"
        message="This hides the category. Existing entries with this category are not affected."
        confirmLabel="Remove"
        onConfirm={()=>removeCat.mutate(deleteCat)}
        onCancel={()=>setDeleteCat(null)}
      />

      {/* Delete user confirm */}
      <ConfirmDialog
        open={!!deleteUser}
        title={`Remove ${deleteUser?.full_name}?`}
        message="The user will no longer be able to log in. Their existing entries remain intact."
        confirmLabel="Remove user"
        onConfirm={()=>removeUser.mutate(deleteUser?.id)}
        onCancel={()=>setDeleteUser(null)}
      />

      {/* Edit team member modal */}
      {editUser&&(
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:380}}>
            <div className="modal-title">Edit member — {editUser.full_name}</div>
            <div className="field">
              <label>Full name</label>
              <input value={editUserForm.full_name} onChange={e=>setEditUserForm(f=>({...f,full_name:e.target.value}))} autoFocus/>
            </div>
            <div className="field">
              <label>Username (login ID)</label>
              <input value={editUserForm.username} onChange={e=>setEditUserForm(f=>({...f,username:e.target.value}))} placeholder="e.g. priya"/>
            </div>
            <div className="field">
              <label>Role</label>
              <select value={editUserForm.role} onChange={e=>setEditUserForm(f=>({...f,role:e.target.value}))}>
                <option value="team">Team — can log work, view own entries</option>
                <option value="admin">Admin — HR access + manage entries</option>
                <option value="accountant">Accountant — invoices &amp; finance only</option>
              </select>
            </div>
            <div className="field">
              <label>Date of birth</label>
              <input type="date" value={editUserForm.date_of_birth} onChange={e=>setEditUserForm(f=>({...f,date_of_birth:e.target.value}))} style={{maxWidth:200}}/>
            </div>
            <div className="modal-actions">
              <button onClick={()=>setEditUser(null)}>Cancel</button>
              <button className="btn-primary" onClick={()=>{
                if(!editUserForm.full_name||!editUserForm.username){toast.error('Name and username are required');return;}
                updateUser.mutate({id:editUser.id,...editUserForm});
              }} disabled={updateUser.isPending}>
                {updateUser.isPending?'Saving…':'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
